// =============================================================================
// SimSense Platform — Global Leaderboard Page
// =============================================================================

import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { formatLapTime } from "../utils/formatters";
import { api } from "../utils/api";
import { Select, type SelectOption } from "../components/Select";

export function Leaderboard() {
  const [entries, setEntries] = useState<any[]>([]);
  const [metadata, setMetadata] = useState<{ 
    tracks: string[], 
    cars: string[], 
    seasons: string[],
    hierarchy: Record<string, string[]>
  }>({ tracks: [], cars: [], seasons: [], hierarchy: {} });
  
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<"best_lap" | "avg_30">("best_lap");
  const [filters, setFilters] = useState({ track: "", car: "", season: "", series: "" });

  useEffect(() => {
    document.title = "Leaderboard | SimSense";
    async function fetchMetadata() {
      try {
        const data = await api<any>("/api/leaderboard/metadata");
        if (data) setMetadata(data);
      } catch (err) {
        console.error("Failed to fetch leaderboard metadata", err);
      }
    }
    fetchMetadata();
  }, []);

  useEffect(() => {
    async function fetchLeaderboard() {
      setLoading(true);
      try {
        const query = new URLSearchParams({ 
          ...filters, 
          sortBy,
          // If series is selected, the car filter should either be empty or constrained
          series_id: filters.series
        }).toString();
        const data = await api<any[]>(`/api/leaderboard?${query}`);
        setEntries(data || []);
      } catch (err) {
        console.error("Failed to fetch leaderboard", err);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    }
    fetchLeaderboard();
  }, [filters, sortBy]);

  return (
    <div>
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h2 style={{ marginBottom: "8px" }}>Global Leaderboard</h2>
          <p style={{ color: "var(--text2)", fontSize: "14px" }}>Top stint performances across the SimSense community.</p>
        </div>
        <div style={{ display: "flex", background: "var(--card)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", padding: "2px" }}>
          <button 
            onClick={() => setSortBy("best_lap")}
            style={{ 
              padding: "6px 12px", 
              fontSize: "12px", 
              fontWeight: 600, 
              border: "none", 
              borderRadius: "4px",
              cursor: "pointer",
              background: sortBy === "best_lap" ? "var(--accent)" : "transparent",
              color: sortBy === "best_lap" ? "#fff" : "var(--text2)"
            }}
          >
            Fastest Lap
          </button>
          <button 
            onClick={() => setSortBy("avg_30")}
            style={{ 
              padding: "6px 12px", 
              fontSize: "12px", 
              fontWeight: 600, 
              border: "none", 
              borderRadius: "4px",
              cursor: "pointer",
              background: sortBy === "avg_30" ? "var(--accent)" : "transparent",
              color: sortBy === "avg_30" ? "#fff" : "var(--text2)"
            }}
          >
            Best 30 Avg
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
        <Select
          value={filters.track}
          onChange={v => setFilters(prev => ({ ...prev, track: v }))}
          options={[
            { value: "", label: "All Tracks" },
            ...(metadata?.tracks || []).map(t => ({ value: t, label: t }))
          ]}
          size="sm"
          style={{ flex: 1 }}
        />

        <Select
          value={filters.series}
          onChange={v => setFilters(prev => ({ ...prev, series: v, car: "" }))}
          options={[
            { value: "", label: "All Series" },
            ...Object.keys(metadata?.hierarchy || {}).map(s => ({ value: s, label: s }))
          ]}
          size="sm"
          style={{ flex: 1 }}
        />

        <CarSelect
          value={filters.car}
          onChange={v => setFilters(prev => ({ ...prev, car: v }))}
          series={filters.series}
          metadata={metadata}
        />

        <Select
          value={filters.season}
          onChange={v => setFilters(prev => ({ ...prev, season: v }))}
          options={[
            { value: "", label: "All Seasons" },
            ...(metadata?.seasons || []).map(s => ({ value: s, label: s }))
          ]}
          size="sm"
          style={{ flex: 1 }}
        />
      </div>

      {/* Leaderboard Table */}
      <div style={{ background: "var(--card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)", background: "rgba(255,255,255,0.02)" }}>
              <th style={{ padding: "12px 16px" }}>Pos</th>
              <th style={{ padding: "12px 16px" }}>Driver</th>
              <th style={{ padding: "12px 16px" }}>Track</th>
              <th style={{ padding: "12px 16px" }}>Car</th>
              <th style={{ padding: "12px 16px" }}>{sortBy === "best_lap" ? "Best Lap" : "Avg 30"}</th>
              <th style={{ padding: "12px 16px" }}>Date</th>
              <th style={{ padding: "12px 16px" }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: "40px", textAlign: "center" }}>Loading leaderboard...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: "40px", textAlign: "center" }}>No entries found for this combo.</td></tr>
            ) : (
              entries.map((entry, i) => (
                <tr key={entry.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "12px 16px", fontWeight: 600, color: i < 3 ? "var(--accent)" : "inherit" }}>
                    #{i + 1}
                  </td>
                  <td style={{ padding: "12px 16px", fontWeight: 500 }}>
                    <Link to={`/profile/${entry.userId}`} style={{ color: "inherit", textDecoration: "none" }}>
                      <div style={{ color: "var(--accent)", cursor: "pointer" }}>{entry.driver}</div>
                    </Link>
                    {entry.isCommercial && (
                      <div title={entry.commercialBrand ? `Official setup by ${entry.commercialBrand}` : "Commercial setup"} style={{
                        fontSize: "9px", background: "rgba(245, 158, 11, 0.12)", color: "#fbbf24",
                        padding: "1px 6px", borderRadius: "3px", fontWeight: 600, display: "inline-flex",
                        alignItems: "center", gap: "3px", marginTop: "2px", border: "1px solid rgba(245, 158, 11, 0.2)",
                      }}>
                        <span style={{ fontSize: "9px" }}>&#x2606;</span> {entry.commercialBrand || "Commercial"}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px", color: "var(--text2)" }}>{entry.track}</td>
                  <td style={{ padding: "12px 16px", color: "var(--text2)" }}>
                    <div style={{ fontWeight: 500, color: "var(--text1)" }}>{entry.car}</div>
                    <div style={{ fontSize: "10px", opacity: 0.6 }}>{entry.season}</div>
                  </td>
                  <td style={{ padding: "12px 16px", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                    {formatLapTime(sortBy === "best_lap" ? entry.bestLap : entry.lapAverages?.["30"])}
                  </td>
                  <td style={{ padding: "12px 16px", color: "var(--text2)", fontSize: "12px" }}>
                    {new Date(entry.sessionDate).toLocaleDateString()}
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <Link 
                      to={`/session/${entry.sessionId}`}
                      style={{ color: "var(--accent)", textDecoration: "none", fontSize: "11px", fontWeight: 600 }}
                    >
                      VIEW SESSION →
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Car filter select with grouped options by series */
function CarSelect({ value, onChange, series, metadata }: { value: string; onChange: (v: string) => void; series: string; metadata: any }) {
  const options = useMemo(() => {
    const opts: SelectOption[] = [{ value: "", label: "All Cars" }];
    if (series) {
      (metadata?.hierarchy?.[series] || []).forEach((c: string) => opts.push({ value: c, label: c }));
    } else {
      const hierarchy = metadata?.hierarchy || {};
      Object.entries(hierarchy).forEach(([seriesName, cars]) => {
        if (Array.isArray(cars)) {
          cars.forEach(c => opts.push({ value: c, label: c, group: seriesName }));
        }
      });
      const allGrouped = Object.values(hierarchy).flat();
      (metadata?.cars || []).filter((c: string) => !allGrouped.includes(c)).forEach((c: string) => {
        opts.push({ value: c, label: c, group: "Other Cars" });
      });
    }
    return opts;
  }, [series, metadata]);

  return <Select value={value} onChange={onChange} options={options} size="sm" style={{ flex: 1 }} />;
}
